import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/adminApi';
import type { ExternalLinkStatsDto, PlatformStatsDto } from '../../../services/adminApi.types';
import './ExternalLinksTab.css';

export const ExternalLinksTab: React.FC = () => {
  const [stats, setStats] = useState<ExternalLinkStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [daysBack, setDaysBack] = useState<number>(30);

  useEffect(() => {
    loadStats();
  }, [period, daysBack]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const data = await adminApi.getExternalLinkStats(
        period,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );

      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (platformStats: PlatformStatsDto[]): { [platform: string]: number } => {
    const totals: { [platform: string]: number } = {};
    platformStats.forEach(ps => {
      totals[ps.platform] = ps.timeSeries.reduce((sum, ts) => sum + ts.clickCount, 0);
    });
    return totals;
  };

  if (loading) {
    return <div className="external-links-loading">Loading statistics...</div>;
  }

  if (error) {
    return (
      <div className="external-links-error">
        <h3>Error Loading Statistics</h3>
        <p>{error}</p>
        <button onClick={loadStats}>Retry</button>
      </div>
    );
  }

  if (!stats || stats.platformStats.length === 0) {
    return <div className="external-links-empty">No click data available for the selected period.</div>;
  }

  const totals = calculateTotals(stats.platformStats);
  const grandTotal = Object.values(totals).reduce((sum, val) => sum + val, 0);

  return (
    <div className="external-links-tab">
      <div className="external-links-header">
        <h2>External Link Click Statistics</h2>
        <div className="external-links-controls">
          <label>
            Period:
            <select value={period} onChange={(e) => setPeriod(e.target.value as 'daily' | 'weekly' | 'monthly')}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          <label>
            Days Back:
            <select value={daysBack} onChange={(e) => setDaysBack(Number(e.target.value))}>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
            </select>
          </label>
          <button onClick={loadStats}>Refresh</button>
        </div>
      </div>

      <div className="external-links-summary">
        <h3>Total Clicks by Platform ({stats.startDate} to {stats.endDate})</h3>
        <table className="external-links-table">
          <thead>
            <tr>
              <th>Platform</th>
              <th>Total Clicks</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(totals)
              .sort((a, b) => b[1] - a[1])
              .map(([platform, count]) => (
                <tr key={platform}>
                  <td className="platform-name">{platform}</td>
                  <td className="click-count">{count.toLocaleString()}</td>
                  <td className="percentage">{((count / grandTotal) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            <tr className="total-row">
              <td><strong>Total</strong></td>
              <td><strong>{grandTotal.toLocaleString()}</strong></td>
              <td><strong>100%</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="external-links-timeline">
        <h3>Click Timeline ({period})</h3>
        <div className="timeline-table-container">
          <table className="external-links-timeline-table">
            <thead>
              <tr>
                <th>Date</th>
                {stats.platformStats.map(ps => (
                  <th key={ps.platform}>{ps.platform}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {/* Aggregate all dates from all platforms */}
              {Array.from(
                new Set(
                  stats.platformStats.flatMap(ps => ps.timeSeries.map(ts => ts.date))
                )
              )
                .sort((a, b) => String(b).localeCompare(String(a))) // Reverse chronological
                .map(date => {
                  const rowData = stats.platformStats.map(ps => {
                    const dataPoint = ps.timeSeries.find(ts => ts.date === date);
                    return dataPoint?.clickCount || 0;
                  });
                  const rowTotal = rowData.reduce((sum, val) => sum + val, 0);

                  return (
                    <tr key={date}>
                      <td className="date-cell">{date}</td>
                      {rowData.map((count, idx) => (
                        <td key={idx} className="count-cell">
                          {count > 0 ? count : '-'}
                        </td>
                      ))}
                      <td className="total-cell"><strong>{rowTotal}</strong></td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
